import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  generateTrackingId,
  URLS,
  useCallIntelDefaultsShallowStore,
  useCallIntelNavigator,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useIsSmallMode } from '@frontend/shared';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { CallIntelDemoBanner } from '../components/call-intelligence/call-intel-demo-banner';
import { CallRecordingsBanner } from '../components/call-intelligence/call-recordings-banner';
import { MIN_SEARCH_LENGTH } from '../components/call-intelligence/filters/constants';
import { CallIntelFollowUpFilters } from '../components/call-intelligence/filters/organisms/call-intel-follow-up-filters';
import type { FilterValues } from '../components/call-intelligence/filters/types';
import { FiltersSection } from '../components/call-intelligence/filters-section';
import { FollowUpsDashboard } from '../components/call-intelligence/follow-ups-dashboard';
import { useActiveFilters } from '../components/call-intelligence/hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../components/call-intelligence/hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../components/call-intelligence/hooks/use-call-intel-locations';
import { useCallIntelShallowStore } from '../components/call-intelligence/hooks/use-call-intel-store';
import { useLocationsMatch } from '../components/call-intelligence/hooks/use-locations-match';
import { useOfficeUsers } from '../components/call-intelligence/hooks/use-office-users';
import { SubViewHeader } from '../components/call-intelligence/sub-view-header';
import { ScrollClassTrigger } from '../components/scroll-class-trigger';
import { URLs } from '../constants';
import { trackingIds } from '../tracking-ids';
import { getFilteredEnumValues } from '../utils/call-intelligence';
import { callIntelResponsiveStyles } from './common-styles';

type Props = {
  type: string;
};

const DEFAULT_SEARCH_TYPE = CallIntelTypes.SearchTypeEnum.SEARCH_BY_NAME;

const validTaskTypes = getFilteredEnumValues(TaskType, TaskType.TYPE_UNKNOWN).concat('all');

const TASK_TYPE_PREFIX = 'TYPE_';

export const CallIntelFollowUps: FC<React.PropsWithChildren<Props>> = ({ type }: Props) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { isSmallMode } = useIsSmallMode();

  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { isDemoAccount, isDemoModal } = useCallIntelDemoFlags();
  const { subView } = useCallIntelShallowStore('subView');
  const { hasValidSubView } = useCallIntelNavigator();

  const { filters, filterHintText, updateFilterValues } = useActiveFilters();

  const [searchQuery, setSearchQuery] = useState<CallIntelTypes.SearchState>({
    query: '',
    value: '',
    searchType: DEFAULT_SEARCH_TYPE,
  });
  const [sortOption, setSortOption] = useState<CallIntelTypes.CallIntelSortOption>('lastCallLatest');

  const { isMultiLocation, locations } = useCallIntelLocations();
  const areLocationsMatching = useLocationsMatch(locations);

  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const taskType = type == 'all' ? '' : `${TASK_TYPE_PREFIX}${type.toUpperCase()}`;
  const taskTypeLabel = dataLabels?.taskTypes?.[taskType] ?? '';
  const pageLabel = taskTypeLabel ? t('{{taskTypeLabel}} Follow-ups', { taskTypeLabel }) : t('All Follow-ups');

  const { activeOfficeUsersForFilter } = useOfficeUsers({
    isDemoLocation: filters.locations?.[0]?.includes('demo'),
    locationIds: filters.locations || [],
  });

  const getPageSubtitleText = () =>
    `${
      isMultiLocation && filters.locations?.length
        ? filters.locations.length === Object.keys(locations).length
          ? t('all locations, ')
          : filters.locations.length === 1
          ? t('1 location, ')
          : t('{{count}} locations, ', { count: filters.locations.length })
        : ''
    }${filterHintText}`;

  const handleSearchOnChange = useCallback(
    (value: string, debounced?: boolean) => {
      const nextSearchType = searchQuery.searchType ?? DEFAULT_SEARCH_TYPE;

      if (!debounced) {
        if (value == '') {
          setSearchQuery({ query: '', value: '', searchType: nextSearchType });
        } else {
          setSearchQuery((prev) => ({ ...prev, value }));
        }
      } else {
        if (value.length >= MIN_SEARCH_LENGTH) {
          setSearchQuery({ value, query: value, searchType: nextSearchType });
        } else {
          setSearchQuery((prev) => ({ ...prev, value }));
        }
      }
    },
    [searchQuery.searchType]
  );

  useEffect(() => {
    if (!areLocationsMatching) {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
      return;
    }
  }, [areLocationsMatching, navigate]);

  useEffect(() => {
    if (!validTaskTypes.includes(taskType) && type !== 'all') {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
      return;
    }

    // If search params exist, allow useInitCallIntelFilters to hydrate the store first
    if (!hasValidSubView(subView)) {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
      return;
    }

    const filtersToUpdate: FilterValues = {};
    // In case of multi location comparison chart click, the search param will have the clicked location id
    // and the filters state will be updated accordingly
    if (search?.locationId) {
      filtersToUpdate.locations = [search.locationId as string];
    }

    updateFilterValues(filtersToUpdate);

    // Using it to avoid multiple api calls on initial load
    setFiltersReady(true);
    // Only depend on primitive values, not object references to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateFilterValues,
    navigate,
    search?.locationId,
    dataLabels,
    taskType,
    type,
    hasValidSubView,
    search,
    subView?.type,
    subView?.id,
  ]);

  return (
    <>
      <CallIntelDemoBanner />
      <CallRecordingsBanner />
      {/* This is used to apply shadow class on header in small mode on scroll */}
      <ScrollClassTrigger targetRef={headerRef} outOfViewClassName='sticky-shadow' />
      <Page css={callIntelResponsiveStyles.page} maxWidth='100%'>
        <SubViewHeader
          headerRef={headerRef}
          title={pageLabel}
          subtitle={t('Showing results for {{time}}', { time: getPageSubtitleText() })}
          breadcrumbs={[
            {
              label: isDemoModal ? '' : t('Analytics'),
              to: '/analytics/dashboard',
            },
            {
              label: t('Call Intelligence'),
              to: URLs.CALL_INTEL_MAIN_PAGE,
            },
            {
              label: pageLabel,
            },
          ]}
          feedbackTrackingId={trackingIds.callIntel.generalFeedbackFollowUpsPage}
        />
        <Page.Body>
          <FiltersSection>
            <CallIntelFollowUpFilters>
              <CallIntelFollowUpFilters.Filters
                officeUsers={activeOfficeUsersForFilter}
                trackingIdBase={generateTrackingId({
                  component: 'follow-up-page',
                  context: 'task-status-filter-btn-click',
                  isDemoAccount,
                })}
                sortOption={sortOption}
                onSortOptionChange={setSortOption}
                hideFilters={taskType ? ['taskTypes'] : []}
                searchAction={{
                  show: isSmallMode,
                  searchValue: searchQuery.value,
                  trackingId: trackingIds.callIntel.followUpPageSearch,
                  onChange: handleSearchOnChange,
                }}
              />
              <CallIntelFollowUpFilters.Actions />
            </CallIntelFollowUpFilters>
          </FiltersSection>
          {filtersReady && (
            <section css={callIntelResponsiveStyles.contentWrapper}>
              <FollowUpsDashboard
                isDrillDown={!!taskType}
                drillDownTaskType={taskType as TaskType}
                searchQuery={searchQuery}
                onSearchQueryChange={handleSearchOnChange}
                sortOption={sortOption}
                onSortOptionChange={setSortOption}
              />
            </section>
          )}
        </Page.Body>
      </Page>
    </>
  );
};
